import { useState, createContext, useEffect } from 'react';
import Layout from 'layouts/purchases/pages/allLeads/layout/index.js';
import { useQuery } from '@tanstack/react-query';
import AaroLoadingScreen from 'components/Loading';
import MDBox from 'components/MDBox';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { getWooOrders } from 'services/outside/woo/orders.js';
import { getParameters } from 'services/api/general/parameter';
import { getPurchases } from 'services/api/purchases';
import { printShippingLabel } from 'layouts/purchases/helpers/printTemplate';

import { subDays, format } from 'date-fns';
import { tr } from 'date-fns/locale';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import { isMobile } from 'helpers/smallCodes';
import ArasShipments from 'components/General/Shipments/Aras';
import SinglePurchasePage from 'layouts/purchases/pages/singleLead/index.js';

// Icons
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PrintIcon from '@mui/icons-material/Print';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

export const AllPurchasesContext = createContext();

// Custom Toolbar Component with Bulk Print
function CustomToolbar(props) {
    const { selectedRows = [], onBulkPrint } = props;

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: '16px 20px',
                borderBottom: '1px solid #E2E8F0',
                backgroundColor: '#FFFFFF',
            }}
        >
            <GridToolbar />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Test button - always visible */}
                <Button
                    variant="outlined"
                    size="small"
                    sx={{
                        fontSize: '0.75rem',
                        color: '#64748B',
                        borderColor: '#CBD5E1',
                        borderRadius: '8px',
                        '&:hover': {
                            borderColor: '#94A3B8',
                            backgroundColor: '#F8FAFC',
                        },
                    }}
                >
                    Seçili: {selectedRows.length}
                </Button>

                {selectedRows.length > 0 && (
                    <Button
                        variant="contained"
                        startIcon={<LocalShippingOutlinedIcon />}
                        onClick={onBulkPrint}
                        sx={{
                            bgcolor: '#0EA5E9',
                            color: 'white',
                            fontWeight: '500',
                            textTransform: 'none',
                            borderRadius: '8px',
                            px: 3,
                            py: 1.5,
                            fontSize: '0.875rem',
                            fontFamily: '"Inter", sans-serif',
                            '&:hover': {
                                bgcolor: '#0284C7',
                            },
                            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                        }}
                    >
                        {selectedRows.length} Sipariş İçin Kargo Etiketi Yazdır
                    </Button>
                )}
            </Box>
        </Box>
    );
}

const fetchParameters = async (keys) => {
    const queryString = keys
        .split(',')
        .map((key) => `key=${key}`)
        .join('&');
    const response = await getParameters(queryString).then((rp) => (rp.data?.result ? rp.data?.result : []));
    return response;
};

const fetchWooOrders = async (
    consumerKey,
    consumerSecret,
    baseUrl,
    days,
    searchTerm = '',
    statusFilter = 'all',
    paymentMethodFilter = 'all',
    customDateRange = null
) => {
    try {
        console.log('fetchWooOrders başlatılıyor:', {
            baseUrl,
            days,
            searchTerm,
            statusFilter,
            paymentMethodFilter,
            customDateRange,
            hasConsumerKey: !!consumerKey,
            hasConsumerSecret: !!consumerSecret,
        });

        const [allOrders, purchases] = await Promise.all([
            (async () => {
                // Date range calculation
                let startDate, endDate;
                if (customDateRange && customDateRange.startDate && customDateRange.endDate) {
                    startDate = new Date(customDateRange.startDate).toISOString();
                    endDate = new Date(customDateRange.endDate).toISOString();
                } else {
                    startDate = new Date(subDays(new Date(), days)).toISOString();
                    endDate = new Date().toISOString();
                }

                let page = 1;
                let allOrders = [];
                let hasMore = true;

                console.log('WooCommerce sorgusu parametreleri:', {
                    startDate,
                    endDate,
                    page,
                    statusFilter,
                    paymentMethodFilter,
                });

                while (hasMore) {
                    const params = {
                        after: startDate,
                        before: endDate,
                        per_page: 100,
                        page: page,
                        search: searchTerm,
                    };

                    // Status filter
                    if (statusFilter !== 'all') {
                        if (statusFilter === 'active') {
                            params.status = ['processing', 'pending', 'completed'];
                        } else {
                            params.status = [statusFilter];
                        }
                    } else {
                        params.status = ['processing', 'completed', 'pending', 'on-hold'];
                    }

                    console.log(`Sayfa ${page} için WooCommerce isteği yapılıyor...`);

                    try {
                        const response = await getWooOrders(consumerKey, consumerSecret, baseUrl, params);
                        console.log('WooCommerce yanıtı:', {
                            status: response.status,
                            statusText: response.statusText,
                            headers: response.headers,
                            dataLength: response.data?.length,
                            firstRecord: response.data?.[0],
                        });

                        if (response.data && response.data.length > 0) {
                            let filteredOrders = response.data;

                            // Payment method filter
                            if (paymentMethodFilter !== 'all') {
                                filteredOrders = filteredOrders.filter(
                                    (order) => order.payment_method === paymentMethodFilter
                                );
                            }

                            allOrders = [...allOrders, ...filteredOrders];
                            page++;
                        } else {
                            hasMore = false;
                        }
                    } catch (wooError) {
                        console.error('WooCommerce sayfa isteğinde hata:', {
                            page,
                            error: wooError.message,
                            response: wooError.response?.data,
                            status: wooError.response?.status,
                        });
                        throw wooError;
                    }
                }
                return allOrders;
            })(),
            getPurchases()
                .then((response) => {
                    console.log('Purchases yanıtı:', {
                        success: true,
                        dataLength: response.data?.result?.length,
                    });
                    return response.data?.result || [];
                })
                .catch((error) => {
                    console.error('Purchases fetch hatası:', {
                        message: error.message,
                        response: error.response?.data,
                        status: error.response?.status,
                    });
                    return [];
                }),
        ]);

        console.log('Veri birleştirme istatistikleri:', {
            allOrdersLength: allOrders.length,
            purchasesLength: purchases.length,
        });

        const purchaseMap = purchases.reduce((acc, purchase) => {
            if (purchase.wooCommerceID) {
                acc[purchase.wooCommerceID] = purchase;
            }
            return acc;
        }, {});

        const ordersWithPurchases = allOrders.map((order) => ({
            ...order,
            purchase: purchaseMap[order.id] || null,
        }));

        return ordersWithPurchases;
    } catch (error) {
        console.error('fetchWooOrders ana hatası:', {
            name: error.name,
            message: error.message,
            stack: error.stack,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw error;
    }
};

// Status renk mapping - WooCommerce standartları
const getStatusColor = (status) => {
    switch (status) {
        case 'processing':
            return '#10B981'; // Yeşil - Hazırlanıyor
        case 'completed':
            return '#0EA5E9'; // Açık Mavi - Tamamlandı
        case 'pending':
            return '#F59E0B'; // Sarı - Beklemede
        case 'on-hold':
            return '#6B7280'; // Gri - Bekletildi/Ödeme Bekleniyor
        case 'cancelled':
            return '#6B7280'; // Gri - İptal Edildi
        default:
            return '#9CA3AF'; // Açık Gri
    }
};

// Status background renkleri - WooCommerce standartları
const getStatusBgColor = (status) => {
    switch (status) {
        case 'processing':
            return '#D1FAE5'; // Açık Yeşil - Hazırlanıyor
        case 'completed':
            return '#E0F2FE'; // Açık Mavi - Tamamlandı
        case 'pending':
            return '#FEF3C7'; // Açık Sarı - Beklemede
        case 'on-hold':
            return '#F3F4F6'; // Açık Gri - Bekletildi/Ödeme Bekleniyor
        case 'cancelled':
            return '#F3F4F6'; // Açık Gri - İptal Edildi
        default:
            return '#F9FAFB'; // Çok Açık Gri
    }
};

// Status Türkçe çeviri - WooCommerce standartları
const getStatusText = (status) => {
    switch (status) {
        case 'processing':
            return 'Hazırlanıyor';
        case 'completed':
            return 'Tamamlandı';
        case 'pending':
            return 'Ödeme Bekleniyor';
        case 'on-hold':
            return 'Bekletildi';
        case 'cancelled':
            return 'İptal Edildi';
        case 'refunded':
            return 'İade Edildi';
        case 'failed':
            return 'Başarısız';
        default:
            return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Bilinmiyor';
    }
};

// Responsive breakpoint sistemi - CSS tabanlı
const getResponsiveConfig = () => {
    return {
        // CSS Media Queries ile responsive davranış
        flexSystem: true,
    };
};

// Dinamik yükseklik hesaplama fonksiyonu - Sadeleştirilmiş
const useDataGridHeight = () => {
    return 'auto'; // Scroll kaldırmak için auto height
};

// Ana WooCommerce Siparişleri bileşeni
export default function WooOrders() {
    const [showActiveLeads, setShowActiveLeads] = useState(true);
    const [search, setSearch] = useState('');
    const [showShipments, setShowShipments] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Enhanced filtering states
    const [statusFilter, setStatusFilter] = useState('active');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null,
        preset: '15', // '7', '15', '30', '90', 'custom'
    });
    const [sortBy, setSortBy] = useState('date_created');
    const [sortOrder, setSortOrder] = useState('desc');

    // Dinamik yükseklik hook'u
    const gridHeight = useDataGridHeight();

    // Responsive konfigürasyon
    const responsiveConfig = getResponsiveConfig();

    const paramQuery = 'api_woo_url,api_woo_cs,api_woo_ck';
    const {
        data,
        isLoading,
        error,
        refetch: refetchParameters,
    } = useQuery({
        queryKey: ['fetchParameters', paramQuery],
        queryFn: () => fetchParameters(paramQuery),
        staleTime: 600000,
    });

    // Parse parameters into separate variables
    const wooConfig =
        data?.reduce((acc, param) => {
            acc[param.key] = param.value;
            return acc;
        }, {}) || {};

    const { api_woo_url, api_woo_cs, api_woo_ck } = wooConfig;

    // WooCommerce siparişlerini çeken query
    const {
        data: wooOrders,
        isLoading: wooOrdersLoading,
        error: wooOrdersError,
        refetch,
    } = useQuery({
        queryKey: [
            'wooOrders',
            api_woo_url,
            dateRange.preset,
            search.trim(),
            statusFilter,
            paymentMethodFilter,
            dateRange.startDate,
            dateRange.endDate,
        ],
        queryFn: async () => {
            if (!api_woo_url || !api_woo_cs || !api_woo_ck) {
                throw new Error('WooCommerce yapılandırma bilgileri eksik');
            }

            const daysToUse = dateRange.preset === 'custom' ? 15 : parseInt(dateRange.preset);
            const customRange = dateRange.preset === 'custom' ? dateRange : null;

            return fetchWooOrders(
                api_woo_ck,
                api_woo_cs,
                api_woo_url,
                daysToUse,
                search.trim(),
                statusFilter,
                paymentMethodFilter,
                customRange
            );
        },
        enabled: !!(api_woo_url && api_woo_cs && api_woo_ck),
        staleTime: 300000,
        retry: false,
    });

    // Yükleme ve hata kontrolleri
    if (isLoading || wooOrdersLoading) {
        return <AaroLoadingScreen />;
    }

    if (error || wooOrdersError) {
        return (
            <div style={{ padding: '20px', color: 'red' }}>
                <h3>Bağlantı Hatası</h3>
                <p>{error?.message || wooOrdersError?.message}</p>
            </div>
        );
    }

    // DataGrid için satırları hazırla
    const rows = (wooOrders || []).map((order) => ({
        id: order.id || Math.random(), // Fallback ID if order.id is missing
        ...order,
    }));

    // Çoklu sipariş yazdırma fonksiyonu
    const printMultipleOrders = () => {
        if (selectedRows.length === 0) {
            alert('Lütfen yazdırmak istediğiniz siparişleri seçin.');
            return;
        }

        const selectedOrders = rows.filter((row) => selectedRows.includes(row.id));
        selectedOrders.forEach((order, index) => {
            setTimeout(() => {
                printShippingLabel(order);
            }, index * 300); // Her sipariş için 300ms gecikme
        });
    };

    // DataGrid sütunları - fonksiyon olarak tanımlandı
    const getColumns = () => [
        {
            field: 'number',
            headerName: 'Sipariş No',
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                    <Box
                        sx={{
                            p: 0.5,
                            borderRadius: 1,
                            bgcolor: '#F0FDF4',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <ShoppingCartIcon fontSize="small" sx={{ color: '#059669' }} />
                    </Box>
                    <Typography
                        variant="body2"
                        fontWeight="600"
                        color="#374151"
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                        }}
                    >
                        #{params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: 'Durum',
            flex: 0.9,
            minWidth: 130,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Chip
                        label={getStatusText(params.value)}
                        size="small"
                        sx={{
                            fontWeight: '500',
                            fontSize: '0.75rem',
                            height: 26,
                            fontFamily: '"Inter", sans-serif',
                            color: getStatusColor(params.value),
                            backgroundColor: getStatusBgColor(params.value),
                            border: 'none',
                            '& .MuiChip-label': {
                                px: 1,
                                lineHeight: 1.2,
                            },
                        }}
                    />
                </Box>
            ),
        },
        {
            field: 'customer',
            headerName: 'Müşteri',
            flex: 2,
            minWidth: 200,
            valueGetter: (params) => {
                if (!params || !params.row || !params.row.billing) return '';
                const { billing } = params.row;
                return `${billing?.first_name || ''} ${billing?.last_name || ''}`.trim();
            },
            renderCell: (params) => {
                if (!params || !params.row || !params.row.billing) return null;
                const { billing } = params.row;
                const customerName = `${billing?.first_name || ''} ${billing?.last_name || ''}`.trim();
                const initials = customerName
                    .split(' ')
                    .map((name) => name.charAt(0))
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minWidth: 0,
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                variant="body2"
                                fontWeight="500"
                                color="#111827"
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {customerName}
                            </Typography>
                            {billing?.company && (
                                <Typography
                                    variant="caption"
                                    color="#6B7280"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontFamily: '"Inter", sans-serif',
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    }}
                                >
                                    {billing.company}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'contact',
            headerName: 'İletişim',
            width: 100,
            sortable: false,
            renderCell: (params) => {
                if (!params || !params.row || !params.row.billing) return null;
                const { billing } = params.row;
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        {billing?.phone && (
                            <Tooltip title={billing.phone} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => window.open(`tel:${billing.phone}`)}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        color: '#6B7280',
                                        '&:hover': {
                                            bgcolor: '#F3F4F6',
                                            color: '#10B981',
                                        },
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <PhoneIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {billing?.email && (
                            <Tooltip title={billing.email} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => window.open(`mailto:${billing.email}`)}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        color: '#6B7280',
                                        '&:hover': {
                                            bgcolor: '#F3F4F6',
                                            color: '#10B981',
                                        },
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <EmailIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'city',
            headerName: 'Şehir',
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => {
                if (!params || !params.row) return '';
                return params.row.shipping?.city || params.row.billing?.city || '';
            },
            renderCell: (params) => {
                if (!params || !params.row) return null;
                const city = params.row.shipping?.city || params.row.billing?.city || '';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                        <LocationOnIcon fontSize="small" sx={{ color: '#9CA3AF', flexShrink: 0 }} />
                        <Typography
                            variant="body2"
                            fontWeight="400"
                            color="#374151"
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {city}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'total',
            headerName: 'Toplam',
            flex: 1,
            minWidth: 120,
            type: 'number',
            valueGetter: (params) => {
                if (!params || !params.row || !params.row.total) return 0;
                return parseFloat(params.row.total);
            },
            renderCell: (params) => {
                if (!params || !params.row) return null;
                const total = parseFloat(params.row.total || 0);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Typography
                            variant="body2"
                            fontWeight="600"
                            color="#111827"
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                            }}
                        >
                            {total.toLocaleString('tr-TR', {
                                style: 'currency',
                                currency: params.row.currency || 'TRY',
                                minimumFractionDigits: 2,
                            })}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'payment_method_title',
            headerName: 'Ödeme',
            flex: 1.2,
            minWidth: 140,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <PaymentIcon fontSize="small" sx={{ color: '#9CA3AF', flexShrink: 0 }} />
                    <Typography
                        variant="body2"
                        fontWeight="400"
                        color="#374151"
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {params.value || 'N/A'}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'date_created',
            headerName: 'Tarih',
            flex: 1.1,
            minWidth: 130,
            type: 'dateTime',
            valueGetter: (params) => {
                if (!params || !params.value) return new Date();
                return new Date(params.value);
            },
            renderCell: (params) => {
                if (!params || !params.row || !params.row.date_created) return null;
                try {
                    const date = new Date(params.row.date_created);
                    return (
                        <Box
                            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight="500"
                                color="#111827"
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.8rem',
                                    lineHeight: 1.3,
                                }}
                            >
                                {format(date, 'dd MMM yyyy', { locale: tr })}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="#6B7280"
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                }}
                            >
                                {format(date, 'HH:mm', { locale: tr })}
                            </Typography>
                        </Box>
                    );
                } catch (error) {
                    return <Typography variant="body2">-</Typography>;
                }
            },
        },
        {
            field: 'line_items',
            headerName: 'Ürünler',
            flex: 0.8,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                if (!params || !params.value) return null;
                const items = params.value || [];
                const itemCount = items.length;
                const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

                return (
                    <Box
                        sx={{
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight="600"
                            color="#111827"
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.85rem',
                                lineHeight: 1.3,
                            }}
                        >
                            {totalQuantity}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="#6B7280"
                            sx={{
                                fontSize: '0.7rem',
                                fontFamily: '"Inter", sans-serif',
                                lineHeight: 1.2,
                            }}
                        >
                            {itemCount} çeşit
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'İşlem',
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Tooltip title="Detayları Görüntüle" arrow>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setSelectedRow(params.row);
                                setOpenModal(true);
                            }}
                            sx={{
                                width: 32,
                                height: 32,
                                color: '#6B7280',
                                '&:hover': {
                                    bgcolor: '#F3F4F6',
                                    color: '#10B981',
                                },
                                transition: 'all 0.15s ease',
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Kargo Etiketi Yazdır" arrow>
                        <IconButton
                            size="small"
                            onClick={() => printShippingLabel(params.row)}
                            sx={{
                                width: 32,
                                height: 32,
                                color: '#6B7280',
                                '&:hover': {
                                    bgcolor: '#F3F4F6',
                                    color: '#10B981',
                                },
                                transition: 'all 0.15s ease',
                            }}
                        >
                            <LocalShippingOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // Bileşeni render et
    return (
        <AllPurchasesContext.Provider
            value={{
                setShowActiveLeads,
                showActiveLeads,
                search,
                setSearch,
                refetch,
                showShipments,
                setShowShipments,
                selectedRow,
                setSelectedRow,
                openModal,
                setOpenModal,
                selectedRows,
                setSelectedRows,
                // Enhanced filtering
                statusFilter,
                setStatusFilter,
                paymentMethodFilter,
                setPaymentMethodFilter,
                dateRange,
                setDateRange,
                sortBy,
                setSortBy,
                sortOrder,
                setSortOrder,
            }}
        >
            <Layout>
                <MDBox opacity={1} px={2}>
                    {!isMobile() && showShipments && (
                        <MDBox mb={10}>
                            <ArasShipments />
                        </MDBox>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            width: '100%',
                            borderRadius: 3,
                            border: '1px solid #E5E7EB',
                            overflow: 'hidden',
                            backgroundColor: '#FFFFFF',
                            position: 'relative',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            '& .MuiDataGrid-root': {
                                border: 'none',
                                fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
                                fontSize: '0.875rem',
                                backgroundColor: '#FFFFFF',
                            },
                            '& .MuiDataGrid-cell': {
                                borderColor: '#F3F4F6',
                                borderWidth: '0 0 1px 0',
                                padding: '0 16px',
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                height: '60px',
                                minHeight: '60px',
                                maxHeight: '60px',
                                '&[data-field="status"]': {
                                    justifyContent: 'flex-start',
                                },
                                '&[data-field="contact"]': {
                                    justifyContent: 'center',
                                },
                                '&[data-field="actions"]': {
                                    justifyContent: 'center',
                                },
                                '&[data-field="line_items"]': {
                                    justifyContent: 'center',
                                },
                                '&[data-field="total"]': {
                                    justifyContent: 'flex-end',
                                },
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#F8FAFC',
                                color: '#475569',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                borderBottom: '1px solid #E2E8F0',
                                minHeight: '52px !important',
                                '& .MuiDataGrid-columnHeader': {
                                    padding: '0 16px',
                                    outline: 'none',
                                    '&:focus': {
                                        outline: 'none',
                                    },
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: '600',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.3,
                                    color: '#475569',
                                },
                                '& .MuiDataGrid-iconSeparator': {
                                    display: 'none',
                                },
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                backgroundColor: '#FFFFFF',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                backgroundColor: '#F8FAFC',
                                borderTop: '1px solid #E2E8F0',
                                minHeight: '52px',
                                padding: '12px 16px',
                                '& .MuiTablePagination-root': {
                                    color: '#64748B',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.875rem',
                                },
                                '& .MuiTablePagination-select': {
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.875rem',
                                },
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontSize: '0.8rem',
                                    color: '#64748B',
                                },
                            },
                            '& .MuiDataGrid-row': {
                                height: '60px !important',
                                minHeight: '60px !important',
                                maxHeight: '60px !important',
                                borderBottom: '1px solid #F1F5F9',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#F8FAFC',
                                    cursor: 'pointer',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#F0F9FF !important',
                                    borderLeft: '3px solid #0EA5E9',
                                    '&:hover': {
                                        backgroundColor: '#E0F2FE !important',
                                    },
                                },
                                '&:last-child': {
                                    borderBottom: 'none',
                                },
                            },
                            '& .MuiDataGrid-cell:focus': {
                                outline: 'none',
                            },
                            '& .MuiDataGrid-columnHeader:focus': {
                                outline: 'none',
                            },
                            '& .MuiCheckbox-root': {
                                color: '#94A3B8',
                                '&.Mui-checked': {
                                    color: '#0EA5E9',
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(14, 165, 233, 0.04)',
                                },
                            },
                            '& .MuiDataGrid-toolbarContainer': {
                                padding: '16px 20px',
                                borderBottom: '1px solid #E2E8F0',
                                backgroundColor: '#FFFFFF',
                                '& .MuiButton-root': {
                                    color: '#475569',
                                    fontWeight: '500',
                                    fontFamily: '"Inter", sans-serif',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: '#F1F5F9',
                                    },
                                },
                                '& .MuiInputBase-root': {
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '0.875rem',
                                    borderRadius: '8px',
                                },
                                '& .MuiTextField-root': {
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        fontSize: '0.875rem',
                                    },
                                },
                            },
                            // Scrollbar styling
                            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                                width: '6px',
                                height: '6px',
                            },
                            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                                backgroundColor: '#F3F4F6',
                            },
                            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                                backgroundColor: '#D1D5DB',
                                borderRadius: '3px',
                                '&:hover': {
                                    backgroundColor: '#9CA3AF',
                                },
                            },
                            // CSS Media Queries ile responsive davranış
                            '@media (max-width: 768px)': {
                                '& .MuiDataGrid-columnHeader--contact': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-cell--contact': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnHeader--payment_method_title': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-cell--payment_method_title': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnHeader--line_items': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-cell--line_items': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnHeader--city': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-cell--city': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-cell': {
                                    padding: '8px 12px',
                                    fontSize: '0.75rem',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    minHeight: '40px !important',
                                },
                                '& .MuiDataGrid-row': {
                                    minHeight: '50px !important',
                                },
                            },
                            '@media (max-width: 1024px) and (min-width: 769px)': {
                                '& .MuiDataGrid-columnHeader--line_items': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-cell--line_items': {
                                    display: 'none',
                                },
                            },
                        }}
                    >
                        <DataGridPro
                            rows={rows}
                            columns={getColumns()}
                            height={600}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 100,
                                        page: 0,
                                    },
                                },
                                sorting: {
                                    sortModel: [{ field: 'date_created', sort: 'desc' }],
                                },
                                columns: {
                                    columnVisibilityModel: {
                                        // CSS Media Queries ile responsive davranış sağlanacak
                                    },
                                },
                            }}
                            pageSizeOptions={[25, 50, 100, 500]}
                            checkboxSelection
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={(newSelection) => {
                                setSelectedRows(newSelection);
                            }}
                            slots={{
                                toolbar: (toolbarProps) => (
                                    <CustomToolbar
                                        {...toolbarProps}
                                        selectedRows={selectedRows}
                                        onBulkPrint={printMultipleOrders}
                                    />
                                ),
                            }}
                            loading={wooOrdersLoading}
                            rowHeight={60}
                            density="comfortable"
                        />
                    </Paper>
                </MDBox>
            </Layout>
            {selectedRow && (
                <Dialog
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    maxWidth="lg"
                    fullWidth
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '90vw',
                            height: '90vh',
                            maxWidth: 'none',
                            maxHeight: 'none',
                        },
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight="600">
                                Sipariş #{selectedRow.number} - Detayları
                            </Typography>
                            <Button onClick={() => setOpenModal(false)} color="inherit">
                                ✕
                            </Button>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                        <SinglePurchasePage
                            incomingID={selectedRow.purchase?.wooCommerceID || selectedRow.id}
                            isModal={true}
                            wooCommerceOrderDetails={selectedRow}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </AllPurchasesContext.Provider>
    );
}
